import React, { Fragment, useState, useEffect } from 'react';
import { Form, Input, Select, Button, Tooltip, Space, Switch, Flex, Grid } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const DEFAULT_EMAIL_NAMES = [
  { value: 'Personal', icon: '👤' },
  { value: 'Work', icon: '💼' },
  { value: 'Business', icon: '🏢' },
  { value: 'Support', icon: '🛠️' },
  { value: 'Sales', icon: '💰' },
  { value: 'Marketing', icon: '📢' },
  { value: 'Other', icon: '📧' }
];

const { useBreakpoint } = Grid;

/** Stable component so input keeps focus when parent re-renders (must be outside EmailAddresses) */
const EmailRowInput = React.forwardRef((props, ref) => {
  const { value = '', onChange, field, handleTypeChange, typeSelectWidth = 180 } = props;
  return (
    <Space.Compact style={{ display: 'flex', width: '100%' }}>
      <Form.Item
        name={[field.name, 'name']}
        rules={[{ required: props.isRequired, message: 'Type required' }]}
        noStyle
      >
        <Select
          placeholder="Type"
          style={{ width: typeSelectWidth }}
          dropdownStyle={{ minWidth: 220 }}
          onChange={(val) => handleTypeChange(field.key, val)}
          options={DEFAULT_EMAIL_NAMES.map(item => ({
            value: item.value,
            label: `${item.icon} ${item.value}`
          }))}
        />
      </Form.Item>
      <Input
        ref={ref}
        placeholder="example@domain.com"
        style={{ flex: 1 }}
        type="email"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </Space.Compact>
  );
});

EmailRowInput.displayName = 'EmailRowInput';

const EmailAddresses = ({ 
  name = "emails",
  initialValues = null,
  onChange = null,
  showAsFormList = true,
  isRequired = false
}) => {
  const screens = useBreakpoint();
  const isSmallScreen = !screens.md;
  const typeSelectWidth = isSmallScreen ? 90 : 180;

  const [fieldValues, setFieldValues] = useState({});
  const [defaultField, setDefaultField] = useState(0);
  const form = Form.useFormInstance();

  // Ensure form is properly initialized with default values
  useEffect(() => {
    const currentValues = form.getFieldValue(name);
    if (!currentValues || !Array.isArray(currentValues) || currentValues.length === 0) {
      const defaultValues = getInitialValue();
      // Only set default values if isRequired is true or if there are initial values
      if (isRequired || (defaultValues && defaultValues.length > 0)) {
        form.setFieldsValue({
          [name]: defaultValues
        });
      }
    }
  }, [name, form, initialValues, isRequired]);

  const handleTypeChange = (fieldKey, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleDefaultChange = (fieldIndex) => {
    setDefaultField(fieldIndex);

    // Update all other fields' is_default to false (current field is handled by Form.Item)
    const currentValues = form.getFieldValue(name) || [];
    currentValues.forEach((item, index) => {
      if (index !== fieldIndex) {
        form.setFieldValue([name, index, 'is_default'], false);
      }
    });
  };

  const getInitialValue = () => {
    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      // If only one email, ensure it's default
      if (initialValues.length === 1) {
        return [{ ...initialValues[0], is_default: true }];
      }
      return initialValues;
    }
    // Return at least one default email if isRequired is true, otherwise return empty array
    if (isRequired) {
      return [{ name: 'Personal', email: '', is_default: true }];
    }
    return [];
  };

  if (!showAsFormList && onChange) {
    // For components that need custom state management
    return null; // Placeholder for now
  }

  return (
    <Form.List 
      name={name}
      initialValue={getInitialValue()}
    >
      {(fields, { add, remove }) => (
        <Fragment>
          {fields.map((field, index) => {
            const currentType   = fieldValues[field.key] || 'Personal';
            const currentValues = form.getFieldValue(name) || [];
            const isDefault     = currentValues[index]?.is_default || false;
            
            return (
              <div key={`email-${field.key}-${index}`} className="mb-3">
                <Flex justify="space-between" className="">
                  <Flex align="flex-end" justify="space-between" className="mb-2">
                    <div style={{width: '120px'}}>
                      {isDefault ? <small className="text-muted me-2">Email {index + 1} is default</small> : <small className="text-muted me-2">Set email {index + 1} as default</small>}
                    </div>
                    
                    <Form.Item
                      name={[field.name, 'is_default']}
                      noStyle
                      valuePropName="checked"
                    >
                      <Switch 
                        size="small"
                        onChange={(checked) => {
                          if (checked) {
                            handleDefaultChange(index);
                          }
                        }}
                      />
                    </Form.Item>
                  </Flex>
                  <div className="">
                    {(!isRequired || fields.length > 1) && (
                      <Tooltip title="Remove email">
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            remove(field.name);
                            setFieldValues(prev => {
                              const newValues = { ...prev };
                              delete newValues[field.key];
                              return newValues;
                            });
                            if (index === defaultField && fields.length > 1) {
                              setDefaultField(0);
                            } else if (index < defaultField) {
                              setDefaultField(defaultField - 1);
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </Flex>
                
                {/* Form.Item wraps entire row so error message appears at bottom of row */}
                <Form.Item
                  name={[field.name, 'email']}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (isRequired && (!value || !String(value).trim())) {
                          return Promise.reject(new Error('Email address required'));
                        }
                        if (value && String(value).trim()) {
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(String(value).trim())) {
                            return Promise.reject(new Error('Please enter a valid email address'));
                          }
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <EmailRowInput
                    field={field}
                    handleTypeChange={handleTypeChange}
                    isRequired={isRequired}
                    typeSelectWidth={typeSelectWidth}
                  />
                </Form.Item>
              </div>
            );
          })}

          <Form.Item>
            <Button
              type="link"
              size='small'
              onClick={() => {
                const currentFields = fields.length;
                add({ 
                  name: 'Personal', 
                  email: '', 
                  is_default: currentFields === 0 // Set as default if it's the first email
                });
              }}
              icon={<PlusOutlined />}
              className='px-0'
            >
              Add Email Address
            </Button>
          </Form.Item>
        </Fragment>
      )}
    </Form.List>
  );
};

export default EmailAddresses;

